import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { newDocumentSignAction } from 'src/redux/actions/documentationAction';

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  setIsDocSigned: any;
  formSubmissionUuid: string;
  isSupervisor: boolean;
  docType: any
}

const SignVector = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M12.9264 3.05139L14.3326 1.64515C14.9428 1.03495 15.9322 1.03495 16.5424 1.64515C17.1526 2.25534 17.1526 3.24466 16.5424 3.85485L7.69349 12.7037C7.25292 13.1443 6.70953 13.4681 6.11241 13.646L3.875 14.3125L4.54148 12.0751C4.71935 11.478 5.04321 10.9346 5.48377 10.494L12.9264 3.05139ZM12.9264 3.05139L15.125 5.24999M13.875 10.9792V14.9375C13.875 15.973 13.0355 16.8125 12 16.8125H3.25C2.21447 16.8125 1.375 15.973 1.375 14.9375V6.18749C1.375 5.15196 2.21447 4.31249 3.25 4.31249H7.20833"
        stroke="white"
        strokeWidth="1.3125"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const SignDocementModel: FC<SignatureModalProps> = ({
  open,
  onClose,
  formSubmissionUuid,
  isSupervisor,
  setIsDocSigned,
  docType
}) => {
  const dispatch = useDispatch();

  const therapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );

  const newDocumentSignReducer = useSelector((state: any) => state.newDocumentSignReducer);
  const [signatureName, setSignatureName] = useState('');
  const [agree, setAgree] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState({ type: '', message: '' });

  useEffect(() => {
    if (newDocumentSignReducer?.response === null && !newDocumentSignReducer?.isSuccess) return;
    if (newDocumentSignReducer?.response !== null && !newDocumentSignReducer?.isSuccess) {
      //  ERROR OCCURRED
      setPopUpMessage({ message: newDocumentSignReducer?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });
        onClose();
      }, 3000);

      return;
    }

    //SUCCESS signature signed successfully

    setPopUpMessage({ message: newDocumentSignReducer?.response?.message, type: '' });
    setIsDocSigned(newDocumentSignReducer?.response?.data);
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
      onClose();
    }, 3000);
  }, [newDocumentSignReducer]);

  const handleSign = () => {
    if (!signatureName.trim()) return;

    dispatch(
      newDocumentSignAction({
        formSubmissionUuid: formSubmissionUuid,
        signerName: signatureName,
        consentConfirmation: agree,
        isSupervisor: isSupervisor,
        documentType: docType?.replaceAll(' ', '_')?.toUpperCase(),
      })
    );
    setSignatureName('');
  };

  const handleClose = () => {
    setSignatureName('');
    onClose();
  };

  const handleChangesStatus = (e: any) => {
    setAgree(e?.target?.checked);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
    >
      <DialogTitle>Add Signature</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: '20px' }}>
          <Stack
            display={'flex'}
            flexDirection={'row'}
            gap={1}
          >
            <Typography variant="subtitle1">Name:</Typography>
            <Typography variant="body2">
              {therapistProfile?.lastName + ' ' + therapistProfile?.firstName}
            </Typography>
          </Stack>
          <Stack
            display={'flex'}
            flexDirection={'row'}
            gap={1}
          >
            <Typography variant="subtitle1">Credentials:</Typography>
            <Typography variant="body2">{therapistProfile?.credentials}</Typography>
          </Stack>
          <Stack
            display={'flex'}
            flexDirection={'row'}
            gap={1}
          >
            <Typography variant="subtitle1">License #:</Typography>
            <Typography variant="body2">{therapistProfile?.licenseNumber}</Typography>
          </Stack>
        </Box>

        <TextField
          fullWidth
          label="Type your signature"
          placeholder="Type your signature"
          value={signatureName}
          type="text"
          onChange={(e) => setSignatureName(e.target.value)}
          sx={{
            mb: 2,
            '& input': {
              fontSize: 'larger',
              fontStyle: 'oblique',
            },
          }}
        />

        <Stack
          display={'flex'}
          flexDirection={'row'}
          alignItems={'flex-start'}
          width={'420px'}
        >
          <Checkbox
            checked={agree}
            onChange={handleChangesStatus}
          />
          <Typography
            variant="body2"
            flexWrap={'wrap'}
          >
            I agree that my electronic signature is legally binding and holds the same validity as
            my handwritten signature, in accordance with applicable laws and regulations.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSign}
          disabled={!signatureName.trim() || !agree}
          startIcon={
            newDocumentSignReducer?.loading ? <CircularProgress size={20} /> : <SignVector />
          }
        >
          {newDocumentSignReducer?.loading ? 'Signing...' : 'Sign'}
        </Button>
      </DialogActions>
      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}
    </Dialog>
  );
};

export default SignDocementModel;
